<template>
  <ModalBase :show-modal="showModal" :width-md="true" :auto-h="true" @close-modal="close" class="reconciliation-modal">
    <template v-slot:titlebar>
      <TitleBar variant-class="title-modal title-modal--simple">
        <template v-slot:title>
          {{ $gettext('Match transaction') }}
        </template>
      </TitleBar>
    </template>
    <template v-slot:maincontent>
      <div class="current-transaction-wrap">
        <div style="display: flex; justify-content: space-between;">
          <div style="line-height: 30px; color: #b9b4b4;">{{ $gettext('Transaction') }}</div>
          <div class="transaction-type-selector-wrap">
            <BankTransactionsTypeSelector v-if="selectedTransaction.status !== 'others'" v-model="selectedTransaction.status" @type-selected="val => selectedTransaction.status = val" />
          </div>
        </div>
        <div class="current-transaction" :class="[selectedTransaction.type]">
          <div class="transaction-icon">
            <font-awesome-icon icon="fa-regular fa-credit-card" class="d-block" />
          </div>
          <div class="transaction-data-wrap">
            <div class="transaction-data">
              <div class="transaction-concept">
                <span class="transaction-concept--description">{{ selectedTransaction.description }}</span>
                <span v-if="selectedTransaction.name" class="transaction-concept--name">{{ selectedTransaction.name }}</span>
              </div>
              <div class="transaction-date">
                <span class="transaction-date--date">{{ selectedTransaction.date }}</span>
                <span v-if="selectedTransaction.target_account" class="transaction-date--target">{{ selectedTransaction.target_account }}</span>
              </div>
            </div>
            <div class="transaction-amount">
              {{ selectedTransaction.amount }}
            </div>
          </div>
        </div>
      </div>
      <div 
        :class="['connect-to-selector-wrap', {
          'selected-other': selectedTransaction.status === 'others',
          'disabled': disabledSelector,
          'connected': isConnected
        }]"
      >
        <div v-if="isConnected" class="connect-to-connected">
          <div class="connect-status-badge">
            <b-badge variant="primary" pill>
              {{ $gettext('Connected') }}
              <font-awesome-icon icon="fa-solid fa-check" />
            </b-badge>
          </div>
        </div>
        <div v-else class="connect-to-selector">
          <div class="connect-to-selector--left">
            <span class="connect-to-selector--text">{{ $gettext('Connect to') }}</span>
            <GekkoButton 
              @click="setSelectedOption('cost')" 
              :text="$gettext('Cost')" 
              :disabled="selectedTransaction.status === 'others' || disabledSelector" 
              class="selector-btn" 
              :variant="(selectedConnectorType === 'cost') ? 'primary' : 'outline-primary'"
            />
          </div>
          <div class="connect-to-selector--right">
            <GekkoButton 
              @click="setSelectedOption('invoice')" 
              :text="$gettext('Invoice')" 
              :disabled="selectedTransaction.status === 'others' || disabledSelector" 
              class="selector-btn" 
              :variant="(selectedConnectorType === 'invoice') ? 'primary' : 'outline-primary'"
            />
            <span class="connect-to-selector--text">{{ $gettext('or') }}</span>
            <b-form-checkbox v-model="selectedTransaction.status" value="others" unchecked-value="unassigned" :disabled="disabledSelector" name="check-button" switch class="selector-toggle">
              {{ (selectedTransaction.status === 'others') ? $gettext('Marked as other / bulk') : $gettext('Mark as other / bulk')}}
            </b-form-checkbox>
          </div>
        </div>
      </div>

      <div class="connect-to-item-wrap">
        <div class="connect-to-item">

            <div v-if="selectedTransaction.status === 'others'" class="transaction-type-hint">
              {{ $gettext('When you mark a transaction as "other", it means that this transaction is a business transaction but you cannot adequately select a cost or invoice item to link it to. This can for instance happen when multiple payments link to one larger cost item. Gekko will ignore the transactions marked as "other".') }}
            </div>

            <div v-else-if="selectedTransaction.status === 'taxes'" class="transaction-type-hint">
              {{ $gettext('Assigning this transaction to taxes means that this transaction means that these are the taxes you needed to pay based upon what you recorded in Gekko. Marking a transaction as taxes therefore will not retroactively affect your company records and will not make a change in your account.') }}
            </div>

            <div v-else-if="selectedTransaction.status === 'private'" class="transaction-type-hint">
              {{ $gettext('Selecting a private transaction means that this transaction does not affect your company records. For instance when you add or withdraw money from your private bank account or buy something non business related from your business account.') }}
            </div>

            <template v-else>
              <div class="selected-connector-titlebar">
                <span class="selected-connector-titlebar--title">{{ selectedConnectorTitle }}</span>
                <div class="selected-connector-titlebar--actions">
                  <GekkoButton 
                    v-if="selectedConnectorType === 'cost' && connectedView === 'matched_list' && multiSelectEnabled"
                    @click="selectMoreItems" 
                    icon="fa-solid fa-arrow-left-long"
                    :text="$gettext('Select more items')"
                    variant="link"
                    class="text-brand"
                  />
                  <GekkoButton 
                    v-if="isConnected && (connectedView === 'list' || connectedView === 'suggested') && multiSelectEnabled"
                    @click="connectedView = 'matched_list'" 
                    icon="fa-solid fa-arrow-right-long"
                    :text="$gettext('Matched items list')"
                    :icon-right="true"
                    variant="link"
                    class="text-brand"
                  />
                  <GekkoButton 
                    v-if="suggestedItems && suggestedItems.length > 0 && connectedView !== 'suggested' && !isConnected"
                    @click="connectedView = 'suggested'" 
                    :text="$gettext('See suggested items')"
                    variant="link"
                    class="text-brand"
                  />
                  <GekkoButton 
                    v-if="connectedView === 'suggested'"
                    @click="connectedView = 'list';" 
                    :text="$gettext('See all items')"
                    variant="link"
                    class="text-brand"
                  />
                  <GekkoButton 
                    v-if="(connectedView === 'create' || connectedView === 'edit' || connectedView === 'details') && multiSelectEnabled"
                    @click="cancelView" 
                    icon="fa-solid fa-arrow-left-long"
                    :text="$gettext('Back')"
                    variant="link"
                    class="text-brand"
                  />
                  <GekkoButton 
                    v-if="connectedView === 'details' && connectedSelectedItem && selectedTransaction.status === 'matched_with_cost'"
                    @click="connectedView = 'edit'" 
                    icon="fa-solid fa-pen-to-square"
                    :text="$gettext('Edit')" 
                    variant="link"
                    class="text-brand"
                  />
                  <GekkoButton 
                    v-if="connectedView === 'edit'"
                    @click="connectedView = 'details'" 
                    icon="fa-solid fa-arrow-left"
                    :text="$gettext('Details')" 
                    variant="link"
                    class="text-brand px-0"
                  />
                  <GekkoButton 
                    v-if="connectedSelectedItem && selectedTransaction.status === 'matched_with_invoice'"
                    :href="'/invoices/view/' + connectedSelectedItem.id + '/'" 
                    icon="fa-solid fa-arrow-up-right-from-square"
                    :text="$gettext('Details')" 
                    variant="link"
                    class="text-brand px-0"
                  />
                </div>
              </div>

              <div
                v-if="(multiSelectEnabled || !isConnected) && (connectedView === 'list' || connectedView === 'matched_list' || connectedView === 'suggested')"
                class="create-new-actions"
                style="display: flex; justify-content: center; padding-bottom: 0.5rem;"
              >
                <GekkoButton 
                  v-if="selectedConnectorType === 'cost'"
                  @click="connectedView = 'create'" 
                  icon="fa-solid fa-plus"
                  variant="outline-primary"
                  class="w-100"
                  :text="$gettext('Create new cost')" 
                />
                <GekkoButton 
                  v-if="selectedConnectorType === 'invoice'"
                  @click="connectedView = 'create'" 
                  icon="fa-solid fa-plus"
                  variant="outline-primary"
                  class="w-100"
                  :text="$gettext('Create new correction')" 
                />
              </div>

              <div class="connected-wrap">

              

              <template v-if="selectedConnectorType === 'cost'">
                <!-- Edit screen (only for costs) -->
                <div v-if="connectedSelectedItem && connectedView === 'edit'" style="height: 500px;">
                  <EditCost @edited="costEdited" :force-cost-id="connectedSelectedItem.id" />
                </div>

                <template v-else>
                  <!-- Suggested Cost table list -->
                  <BankSuggestedCostTable 
                    v-if="suggestedItems && connectedView === 'suggested'" 
                    :suggested-items="suggestedItems" 
                    @row-clicked="itemRowClick" 
                  />

                  <!-- Cost table list -->
                  <CostTable 
                    v-if="connectedView === 'list'" 
                    :show-totals="false" 
                    :component-on-modal="true" 
                    :modal-temp-cookie="filterCookieName"
                    :selectable-rows="multiSelectEnabled"
                    :disabled-rows="selectedTransaction[transaction_prop_name]"
                    @row-clicked="itemRowClick" 
                    @selected-item="selectedTableItems" 
                    @destroyed="bulkMatchItems = null" 
                  />

                  <!-- Create cost -->
                  <div v-if="connectedView === 'create'" style="height: 500px;">
                    <BankCreateCost @created="costCreated" :transaction-cost="selectedTransaction" />
                  </div>

                  <!-- Matched cost list preview -->
                  <BankMatchedCostTable v-if="isConnected && connectedView === 'matched_list'" :items="connectedItems" @row-clicked="selectConnectedItem" @disconnect-item="disconnectMatchedItem" @edit-item="selectToEdit" />

                  <!-- Cost preview -->
                  <BankMatchedItemPreview v-if="connectedSelectedItem && connectedView === 'details'" :connector-type="selectedConnectorType" :assigned-connector="connectedSelectedItem"  />

                  <BankMatchedAmountsWarning v-if="isConnected && connectedView === 'details'" @set-to-edit="connectedView = 'edit'" :transaction-amount="Math.abs(parseFloat(selectedTransaction.amount))" :matched-items="connectedItems" :connector-type="selectedConnectorType" />
                </template>
              </template>

              <template v-if="selectedConnectorType === 'invoice'">

                <!-- Suggested Invoice table list -->
                <BankSuggestedCostTable 
                  v-if="suggestedItems && connectedView === 'suggested'" 
                  :suggested-items="suggestedItems" 
                  @row-clicked="itemRowClick" 
                />

                <!-- Invoice table list -->
                <InvoicesTable 
                  v-if="connectedView === 'list'" 
                  :show-totals="false" 
                  :component-on-modal="true" 
                  :modal-temp-cookie="filterCookieName"
                  :selectable-rows="multiSelectEnabled"
                  :disabled-rows="selectedTransaction[transaction_prop_name]"
                  :show-recurring="false"
                  @row-clicked="itemRowClick" 
                  @selected-item="selectedTableItems" 
                  @destroyed="bulkMatchItems = null" 
                />

                <!-- Create correction -->
                <div v-if="connectedView === 'create'" class="correction-create-wrap upload-correction-form">
                  <overlay-scrollbars>
                    <EditCorrection @created="correctionCreated" :transaction-invoice="selectedTransaction" />
                  </overlay-scrollbars>
                </div>

                <!-- Invoice preview -->
                <BankMatchedItemPreview v-if="connectedSelectedItem && connectedView === 'details'" :connector-type="selectedConnectorType" :assigned-connector="connectedSelectedItem" />
              </template>



              </div>



            </template>
            
        </div>

      </div>
    </template>
    <template v-slot:actions>
      
      <div style="display: flex; justify-content: space-between;">
        <div>
          <GekkoButton 
            v-if="isConnected"
            @click="disconnectTransaction()"
            :text="$gettext('Disconnect')"
            icon="fa-solid fa-link-slash"
            class="text-danger"
            variant="link"
          />
        </div>
        <div style="display: flex; justify-content: space-between;">
          <GekkoButton 
            @click="closeModal"
            :text="$gettext('Close')"
            variant="outline-primary"
          />
          <GekkoButton 
            v-if="showBulkUpdateBtn"
            @click="bulkUpdate"
            icon="fa-solid fa-arrow-right-long"
            :icon-right="true"
            :text="$gettext('Update match')"
          />
        </div>
      </div>
    </template>
  </ModalBase>
</template>

<script>
import BankTransactionService from '@/store/services/bank.transaction.service.js';
import CostService from "@/store/services/cost.service.js";
import InvoiceService from "@/store/services/invoice.service.js";
import InvoicePaymentsService from "@/store/services/invoice.payments.service.js";
import TitleBar from "@/components/TitleBar";
import EditCost from "@/components/costs/EditCost";
import BankCreateCost from "@/components/bank/ui/BankCreateCost";
import EditCorrection from "@/components/invoices/EditCorrection";
import ModalBase from "@/components/base_components/ModalBase";
import GekkoButton from "@/components/GekkoButton"
import BankTransactionsTypeSelector from "@/components/bank/ui/BankTransactionsTypeSelector"
import BankMatchedItemPreview from "@/components/bank/ui/BankMatchedItemPreview"
import BankMatchedCostTable from "@/components/bank/ui/BankMatchedCostTable"
import BankSuggestedCostTable from "@/components/bank/ui/BankSuggestedCostTable"
import BankMatchedAmountsWarning from "@/components/bank/ui/BankMatchedAmountsWarning"
import CostTable from "@/components/costs/CostTable";
import InvoicesTable from "@/components/invoices/InvoicesTable"

export default {
  name: "BankReconciliationModal",
  components: {
    TitleBar,
    EditCost,
    BankCreateCost,
    EditCorrection,
    ModalBase,
    GekkoButton,
    BankTransactionsTypeSelector,
    BankMatchedItemPreview,
    BankMatchedCostTable,
    BankSuggestedCostTable,
    InvoicesTable,
    BankMatchedAmountsWarning,
    CostTable,
  },
  props: {
    selectedTransaction: {
      type: Object,
      default: () => {}
    },
  },
  data () {
    return {
      showModal: true,
      filterCookieName: 'TempReconFilterOptionsVue2',
      selectedConnectorType: null, // 'invoice', 'cost'
      connectedItems: [],
      connectedSelectedItem: null,
      connectedView: null, // 'matched_list', 'details', 'create', 'edit', 'list', 'suggested'
      bulkMatchItems: null,
      suggestedItems: null,
      multiSelectEnabled: false,
      transaction_prop_name: 'cost_set',
      parentFilters: null
    }
  },
  computed: {
    isConnected () {
      return this.connectedItems.length > 0
    },
    selectedConnectorTitle () {
      let title = ''
      if (this.selectedConnectorType === 'cost') {
        switch (this.connectedView) {
          case 'matched_list':
            title = this.$gettext('Matched costs list')
            break;
          case 'details':
            title = this.$gettext('Cost preview')
            break;
          case 'create':
            title = this.$gettext('New cost')
            break;
          case 'edit':
            title = this.$gettext('Edit cost')
            break;
          case 'suggested':
            title = this.$gettext('Suggested costs')
            break;
          case 'list':
            title = this.$gettext('Select a cost from the list')
            break;
          }
        title = this.$gettext('Cost')
      } 
      if (this.selectedConnectorType === 'invoice') {
        switch (this.connectedView) {
          case 'matched_list':
            title = this.$gettext('Matched invoices list')
            break;
          case 'details':
            title = this.$gettext('Invoice preview')
            break;
          case 'create':
            title = this.$gettext('New invoice')
            break;
          case 'edit':
            title = this.$gettext('Edit invoice')
            break;
          case 'suggested':
            title = this.$gettext('Suggested invoices')
            break;
          case 'list':
            title = this.$gettext('Select an invoice from the list')
            break;
        }
        title = this.$gettext('Invoice')
      } 
      return title
    },
    isBusiness () {
      return (RegExp('matched_with').test(this.selectedTransaction.status) || this.selectedTransaction.status === 'unassigned' || this.selectedTransaction.status === 'others');
    },
    disabledSelector () {
      return (this.selectedTransaction.status === 'taxes' || this.selectedTransaction.status === 'private')
    },
    showBulkUpdateBtn () {
      if (!this.bulkMatchItems) return false
      return !(JSON.stringify(this.bulkMatchItems.slice().sort()) === JSON.stringify(this.selectedTransaction[this.transaction_prop_name].slice().sort()))
    }
  },
  watch: {
    'selectedTransaction.status' (newValue) {
      this.saveSelectedTransaction()
      if (newValue === 'unassigned') this.setInitialConnector()
    }
  },
  created () {
    this.setInitialConnector()
    this.saveParentFilters()
  },
  beforeDestroy () {
    this.resetParentFilters()
  },
  mounted () {
    document.body.classList.add('modal-on')
  },
  methods: {
    saveParentFilters () {
      this.parentFilters = this.$store.getters["filterStore/getFilter"]
    },
    resetParentFilters () {
      const parentDates = {}
      if (this.parentFilters.start_date) parentDates.push({ start_date: this.parentFilters.start_date })
      if (this.parentFilters.end_date) parentDates.push({ end_date: this.parentFilters.end_date })
      this.$store.commit("filterStore/SET_START_END_DATE", parentDates)
    },
    resetModalCookie () {
      const date_from = new Date(this.selectedTransaction.date);
      date_from.setMonth(date_from.getMonth() - 2);

      const defaultFilters = {
        option: 'custom_range',
        start_date: date_from.toISOString().split('T')[0],
        end_date: this.selectedTransaction.date,
        owner_company: window.company_id
      }

      var d1 = new Date (), expire = new Date ( d1 );
      expire.setMinutes ( d1.getMinutes() + 60 * 12 );
      this.$cookies.set(this.filterCookieName + '-' + window.company_id, defaultFilters, expire, '/');
    },
    close () {
      this.$emit('cancel')
      this.closeModal()
    },
    closeModal () {
      document.body.classList.remove('modal-on')
      this.showModal = false
      this.$emit('cancel')
    },
    setSelectedOption (option) {
      this.selectedConnectorType = option
      this.selectedTransProp()
      this.getSuggestedItems()
    },
    setInitialConnector () {
      if (this.isBusiness) {
        if (this.selectedTransaction.status === 'unassigned') {
          this.selectedConnectorType = (this.selectedTransaction.type === 'in') ? 'invoice' : 'cost'
        } else if (this.selectedTransaction.status === 'matched_with_invoice') {
          this.selectedConnectorType = 'invoice'
        } else if (this.selectedTransaction.status === 'matched_with_cost') {
          this.selectedConnectorType = 'cost'
        }
      }
      this.selectedTransProp()
      this.getConnectedItems()
      this.getSuggestedItems()
    },
    selectedTransProp () {
      this.resetModalCookie()
      if (this.selectedConnectorType === 'cost') {
        this.transaction_prop_name = 'cost_set'
      } else {
        this.transaction_prop_name = 'invoicepayment_set'
      }
    },
    getConnectedItems () {
      if (this.selectedTransaction[this.transaction_prop_name].length > 0) {

        const items_promises = []
        this.selectedTransaction[this.transaction_prop_name].forEach(item_id => {
          let item_promise
          if (this.selectedConnectorType === 'cost') {
            item_promise = CostService.retrieve(item_id).then((response) => {
              return response.data
            }).catch((error) => {
              console.log(error)
            })
          } else {
            item_promise = InvoicePaymentsService.retrieveInvoice(item_id).then((response) => {
              return response.data
            }).catch((error) => {
              console.log(error)
            })
          }
          items_promises.push(item_promise)
        })
        Promise.all( items_promises ).then((values) => {
          this.connectedItems = values
          if (this.selectedTransaction[this.transaction_prop_name].length === 1) {
            this.selectConnectedItem(this.connectedItems[0])
          } else {
            this.connectedView = 'matched_list'
          }
        })

      } else {
        this.connectedItems = []
        this.connectedView = 'list'
      }
    },
    getSuggestedItems () {
      if (this.selectedTransaction.status != 'matched_with_invoice' && this.selectedTransaction.status != 'matched_with_cost') {
        const suggestedEndpoint = (this.selectedConnectorType === 'cost') ? 'suggestedCosts' : 'suggestedInvoices'
        BankTransactionService[suggestedEndpoint](this.selectedTransaction.id).then((response) => {
          this.suggestedItems = response.data
          if (this.suggestedItems.length > 0) {
            this.connectedView = 'suggested' 
          } else {
            this.connectedView = 'list' 
          }
        }).catch((error) => {
          console.log(error)
        })
      }
    },
    costEdited () {
      this.getConnectedItems()
    },
    costCreated (cost_item) {
      this.matchWithCost(cost_item.id, true)
    },
    correctionCreated (correction_invoice_item) {
      this.matchWithInvoice(correction_invoice_item, true)
    },
    itemRowClick (item) {
      if (this.selectedConnectorType === 'cost') {
        this.matchWithCost(item.id, true)
      }
      if (this.selectedConnectorType === 'invoice') {
        this.matchWithInvoice(item, true)
      }
    },

    matchWithInvoice (invoice_item, refresh = false) {
      // TODO mixpanel track totalMatchedTransactions

      let total_amount = Math.min(invoice_item.total_amount, this.selectedTransaction.amount)

      if(invoice_item.currency != 'EUR' && invoice_item.total_amount === 0){
        total_amount = this.selectedTransaction.amount
        InvoiceService.convertTotal(invoice_item.id, total_amount).then(() => {
          invoice_item.total_amount = total_amount
        })
      }

      InvoicePaymentsService.list({ invoice: invoice_item.id }).then((response) => {
        const payments = response.data
        let paymentSum = 0
        if (payments.length > 0) {
          payments.forEach(payment => {
            paymentSum += parseFloat(payment.amount);
          })
        }
        if(payments.length === 1 && paymentSum === total_amount) {
            // "perfect match case" we already have 1 payment
            const updatedPayment = payments[0]
            updatedPayment.transaction = this.selectedTransaction.id
            updatedPayment.payment_type = 'bank'
            return InvoicePaymentsService.partial_update(updatedPayment)
        } else {
          // TODO check for overpaid invoices: what we should do?
          return InvoicePaymentsService.create({
            invoice: invoice_item.id,
            amount: total_amount,
            payment_date: this.selectedTransaction.date,
            payment_type: 'bank',
            transaction: this.selectedTransaction.id,
            description: this.selectedTransaction.description
          })
        }
      }).then((response) => {
        this.selectedTransaction.status = 'matched_with_invoice'
        if (!this.selectedTransaction[this.transaction_prop_name].includes(invoice_item)) this.selectedTransaction[this.transaction_prop_name].push(response.data.id)
        if (refresh) this.getConnectedItems()
      }).catch((error) => {
        console.log(error)
      })

    },

    matchWithCost (cost_item, refresh = false) {
      return CostService.addTransaction(cost_item, { transaction: this.selectedTransaction.id }).then(() => {
        this.selectedTransaction.status = 'matched_with_cost'
        if (!this.selectedTransaction[this.transaction_prop_name].includes(cost_item)) this.selectedTransaction[this.transaction_prop_name].push(cost_item)
        if (refresh) this.getConnectedItems()
      }).catch((error) => {
        console.log(error)
      })
    },

    unmatchWithInvoice (item_id, refresh = false) {
      return InvoicePaymentsService.destroy(item_id).then(() => {
        this.setStatusAfterUnmatch(item_id, refresh)
      }).catch((error) => {
        console.log(error)
      })
    },

    unmatchWithCost (item_id, refresh = false) {
      return CostService.removeTransaction(item_id, { transaction: this.selectedTransaction.id }).then(() => {
        this.setStatusAfterUnmatch(item_id, refresh)
      }).catch((error) => {
        console.log(error)
      })
    },

    setStatusAfterUnmatch (item_id, refresh = false) {
      const item_index = this.selectedTransaction[this.transaction_prop_name].indexOf(item_id)
      if (item_index > -1) this.selectedTransaction[this.transaction_prop_name].splice(item_index, 1)
      if (this.selectedTransaction[this.transaction_prop_name].length < 1) this.selectedTransaction.status = 'unassigned'
      if (refresh) this.getConnectedItems()
    },

    bulkUpdate () {
      // get items to add
      const bulk_add_items = this.bulkMatchItems.filter(x => !this.selectedTransaction[this.transaction_prop_name].includes(x))
      const add_promises = (bulk_add_items.length > 0) ? this.bulkMatch(bulk_add_items) : []

      // get items to remove
      const bulk_remove_items = this.selectedTransaction[this.transaction_prop_name].filter(x => !this.bulkMatchItems.includes(x))
      const remove_promises = (bulk_remove_items.length > 0) ? this.bulkUnmatch(bulk_remove_items) : []

      Promise.all([...add_promises, ...remove_promises]).then(() => {
        this.bulkMatchItems = null
        this.getConnectedItems()
      })
    },
    bulkMatch (bulk_items) {
      const items_promises = []
      bulk_items.forEach(matched_item => {
        let item_promise
        if (this.selectedConnectorType === 'cost') {
          item_promise = this.matchWithCost(matched_item)
        } else {
          item_promise = this.matchWithInvoice(matched_item)
        }
        items_promises.push(item_promise)
      });
      return items_promises
    },
    bulkUnmatch (bulk_items, direct = false) {
      const items_promises = []
      bulk_items.forEach(matched_item => {
        let item_promise
        if (this.selectedConnectorType === 'cost') {
          item_promise = this.unmatchWithCost(matched_item)
        } else {
          item_promise = this.unmatchWithInvoice(matched_item)
        }
        items_promises.push(item_promise)
      })
      if (!direct) {
        return items_promises
      } else {
        Promise.all(items_promises).then(() => {
          this.getConnectedItems()
        })
      }
    },

    // Disconnect single row item (for multiSelectEnabled )
    disconnectMatchedItem (payload) {
      if (payload.type === 'cost') {
        this.unmatchWithCost(payload.item.id, true)
      }
    },

    // Disconnect all items
    disconnectTransaction () {
      this.bulkUnmatch(this.selectedTransaction[this.transaction_prop_name], true)
      this.connectedSelectedItem = null
    },

    cancelView () {
      if (this.connectedView === 'create' && !this.isConnected) {
        this.connectedView = 'list'
      } else {
        this.connectedView = 'matched_list'
      }
    },
    selectMoreItems () {
      this.connectedView = 'list'
    },
    saveSelectedTransaction () {
      BankTransactionService.update(this.selectedTransaction).then(() => {
      }).catch((error) => {
        console.log(error)
      })
    },
    selectConnectedItem (item) {
      this.connectedSelectedItem = item
      this.connectedView = 'details'
    },
    selectToEdit (item) {
      this.connectedSelectedItem = item
      this.connectedView = 'edit'
    },
    selectedTableItems (items) {
      this.bulkMatchItems = items
    },
  }
}
</script>

<style lang="scss">
.reconciliation-modal {
  .gk-modal { background: $colorBgSidebar; }
  .titlebar { margin: 0 !important; }
}
</style>
<style lang="scss" scoped>
.reconciliation-modal {
  .current-transaction-wrap {
    display: flex;
    flex-direction: column;
    .transaction-type-selector-wrap {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 0.5rem;
    }
  }
  .current-transaction {
    display: flex;
    border-radius: $radiusLg;
    border: 1px solid $colorBorderDash;
    box-shadow: $contentShadow;
    padding: 1rem;
    width: 100%;
    align-items: center;
    background: white;
    &.in { color: $colorBrand; }
    &.out { color: $colorBrown; }
    .transaction-icon {
      font-size: 54px;
      margin-right: 1rem;
    }
    .transaction-data-wrap {
      display: flex;
      align-items: center;
      flex: 1;
      overflow: hidden;
      .transaction-data {
        display: flex;
        flex: 1;
        flex-direction: column;
        margin-right: 1rem;
        overflow: hidden;
        .transaction-concept {
          width: 100%;
          font-weight: bold;
          line-height: 1.2;
          &--description {
            width: 75%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            display: inline-block;
          }
          &--name {
            color: $colorUnassigned;
            width: 25%;
            padding-left: 1rem;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            display: inline-block;
            text-align: right;
          }
        }
        .transaction-date {
          font-size: 16px;
          color: $colorUnassigned;
          &--target {
            float: right;
            text-transform: uppercase;
          }
        }
      }
      .transaction-amount {
        font-size: 22px;
        font-family: DINNextLTPro-Medium, sans-serif;
      }
    }
  }
  .connect-to-selector-wrap {
    width: 100%;
    // outline: 1px solid red;
    position: relative;
    &:before, &:after {
      position: absolute;
      content: '';
      left: 50%;
      top: 0;
      transform: translateX(-50%);
      width: 1px;
      height: 2rem;
      border-left: 2px dashed $colorBrand;
    }
    &:after { 
      top: auto;
      bottom: 0rem;
    }
    &.connected {
      &:before, &:after {
        border-left: 2px solid $colorBrand;
      }
    }
    &.selected-other, &.disabled {
      .selector-btn,
      .connect-to-selector--text,
      &:before, &:after {
        opacity: 0 !important;
        filter: grayscale(1);
      }
    }
    &.disabled {
      .connect-to-selector {
        padding-top: 0;
        padding-bottom: 0;
      }
      .selector-toggle {
        opacity: 0 !important;
        filter: grayscale(1);
      }
    }
    .connect-to-selector {
      padding: 2rem 1rem;
      display: flex;
      align-items: center;
      &--text {
        padding: 0.5rem 1rem;
        color: $colorUnassigned;
      }
      &--left, &--right {
        display: flex;
        align-items: center;
        width: 50%;
        .selector-btn {
          padding-left: 2rem;
          padding-right: 2rem;
          &.disabled {
            opacity: 0 !important;
            filter: grayscale(1);
          }
        }
      }
      &--left {
        justify-content: flex-end;
        .selector-btn {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }
      }
      &--right {
        justify-content: flex-start;
        .selector-btn {
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        }
        .selector-toggle {

        }
      }
      &.disabled {
        opacity: 0;
        filter: grayscale(1);
      }
    }
    .connect-to-connected{
      padding: 2rem 1rem;
      display: flex;
      justify-content: center;
      align-items: center;
      .connect-status-badge {
        .badge {
          font-size: 18px;
          padding: 0.75rem 1rem;
        }
      }
    }
  }
  .connect-to-item-wrap {
    width: 100%;
    // background: white;
    // border-radius: $radiusLg;
    // border: 1px solid $colorBorderDash;
    // box-shadow: $contentShadow;
    .connect-to-item {
      // padding: 0.5rem;
      position: relative;
      .transaction-type-hint {
        color: $colorUnassigned;
        padding: 0.5rem;
      }
      .connected-wrap {
        background: white;
        border-radius: $radiusLg;
        border: 1px solid $colorBorderDash;
        box-shadow: $contentShadow;
        padding: 0.5rem;
      }
      .os-host {
        // max-height: 300px;
        .os-content { padding: 0 !important; }
      }

      .correction-create-wrap {
        height: 500px;
      }

    }
  }
}


.selected-connector-titlebar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  // padding-bottom: 0.5rem;
  // margin-bottom: 0.5rem;
  // border-bottom: 1px solid $colorBorderDash;
  margin-top: -36px;
  &--title {
    font-size: 18px;
    color: $colorUnassigned;
    padding-bottom: 0.5rem;
  }
  &--actions {
  }
}

</style>
